import { createRouter, createWebHistory } from 'vue-router';
import MyCourses from './views/MyCourses.vue';
import AddCourses from './views/AddCourses.vue';
import AddCoursesV2 from './views/AddCoursesV2.vue';
import WelcomeDesktop from './views/WelcomeDesktop.vue';
import Callback from './views/Callback.vue';
import ErrorNotFound from './error/404error.vue';
import ServerError from './error/500error.vue';
import BrowserError from './error/BrowserError.vue';
import ConnectorService from './services/connector.service';
import ConfirmRegistrationv2 from './views/ConfirmRegistrationv2.vue';
import Registerv2 from './components/Registerv2.vue';
import SuccessRegistration from './views/SuccessRegistration.vue';
import idVerify from './views/IDVerify.vue';
import idVerifyReturn from './views/IDVerifyReturn.vue';
import Profile from './views/Profile.vue';
import CourseCheckoutStripe from './views/CourseCheckoutStripe.vue';
import CourseCheckoutCompletion from './components/CourseCheckoutCompletion.vue';
import DashboardWrapper from './views/DashboardWrapper.vue';
import AddToTranscript from './components/add-to-transcript/AddToTranscript.vue';
import Pathways from './views/Pathways.vue';
import PathwayDetail from './views/PathwayDetail.vue';
import AdminOnly2026 from './views/AdminOnly2026.vue';
import auth from './services/auth-manager.service';
import { isFeatureEnabled } from '../feature-flags';
import { useMinorConsentStore } from './stores/minorConsent';
import { consumePartnerHostParam } from './util/partner-host';
import { getRetiredHostRedirectUrl } from './util/retired-hosts';

/**
 * Whether the minor-consent gate should block this navigation. Pure decision so
 * it can be unit-tested without spinning up the router. Blocks only named
 * content routes (unnamed routes like /callback, /logout pass through), never
 * the initial landing (no `fromName`), and never the exempt auth/error routes.
 *
 * ACC-228 follow-up: never blocks during View As. The gate itself is already
 * silently suppressed for an admin viewing-as a minor (App.vue), but this guard
 * reads the store's raw `blocksPortal` independently — without this check it
 * kept cancelling navigation (silent `next(false)`, no error) even though the
 * modal the block exists to protect was never shown.
 * @returns {boolean}
 */
export function shouldBlockNavigationForConsent({ blocksPortal, fromName, toName, exemptRouteNames, isViewingAs }) {
  if (isViewingAs) return false;
  return Boolean(blocksPortal && fromName && toName && toName !== fromName && !exemptRouteNames.includes(toName));
}

// Dev workspaces: apply ?partner_host= and drop it before the router snapshots the URL.
consumePartnerHostParam();

let router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL || '/'),
  routes: [
    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardWrapper
    },
    {
      path: '/',
      name: 'WelcomeDesktop',
      component: WelcomeDesktop
    },
    {
      path: '/pathways',
      name: 'pathways',
      component: Pathways
    },
    {
      path: '/pathways/:interest',
      name: 'pathway-detail',
      component: PathwayDetail,
      props: true
    },
    {
      path: '/register',
      name: 'Registerv2',
      component: Registerv2,
      props: true
    },
    {
      path: '/courses-add',
      name: 'addCourses',
      component: () => Promise.resolve(isFeatureEnabled('MULTI_ENROLLMENT_TYPE') ? AddCoursesV2 : AddCourses),
      props: true
    },
    {
      path: '/courses-checkout',
      name: 'courseCheckout',
      component: CourseCheckoutStripe,
      props: true,
      // Centralised cart-clear on every entry to the Stripe checkout page.
      // The cart-remove call from the view's beforeUnmount hook is unreliable
      // (refreshes, navigates away, and tab closes all skip it), so stale items
      // accumulate and the next checkout attempt is rejected with CART_4004.
      // Calling here covers every navigation source (Dashboard, MyCourses,
      // CourseCardCompact, EnrolledCertificateCard, EnrolledPathwayCard, etc.)
      // in one place. Best-effort: never block navigation if it fails.
      beforeEnter: async (_to, _from, next) => {
        try {
          const { useStripeCheckout } = await import('@/composables/checkout/useStripeCheckout');
          await useStripeCheckout().clearStripeCart();
        } catch (err) {
          console.error('[Stripe] Pre-checkout cart clear failed:', err);
        }
        next();
      }
    },
    {
      path: '/courses-checkout-completion',
      name: 'courseCheckoutCompletion',
      component: CourseCheckoutCompletion,
      props: true
    },
    {
      path: '/my-courses',
      name: 'courseHistory',
      component: MyCourses,
      props: true
    },
    {
      // Redirect the old path so existing links/bookmarks keep working.
      path: '/courses-history',
      redirect: '/my-courses'
    },
    {
      path: '/profile',
      name: 'profile',
      component: Profile
    },
    {
      path: '/signup-contact',
      name: 'login',
      component: WelcomeDesktop
    },
    {
      path: '/login',
      redirect: '/signup-contact'
    },
    {
      path: '/callback',
      component: Callback
    },
    {
      path: '/logout',
      component: Callback
    },
    {
      path: '/confirm-registration',
      name: 'ConfirmRegistrationv2',
      component: ConfirmRegistrationv2
    },
    {
      path: '/success-registration',
      name: 'SuccessRegistration',
      component: SuccessRegistration
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'error',
      component: ErrorNotFound,
      props: true
    },
    {
      path: '/error-page',
      name: 'server-error',
      component: ServerError,
      props: true
    },
    {
      path: '/id-verification',
      name: 'id-verification',
      component: idVerify
    },
    {
      path: '/id-verification/return',
      name: 'id-verification-return',
      component: idVerifyReturn
    },
    {
      path: '/browser-error',
      name: 'browser-error',
      component: BrowserError,
      props: true
    },
    {
      path: '/add-to-transcript',
      name: 'add-to-transcript',
      component: AddToTranscript,
      props: true
    },
    {
      path: '/adminonly/2026',
      name: 'adminOnly2026',
      component: AdminOnly2026,
      beforeEnter: (to, from, next) => {
        if (isFeatureEnabled('PARTNER_SWITCHER')) {
          next();
        } else {
          next('/');
        }
      }
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // Handle hash navigation - don't scroll, let component handle it
    if (to.hash) {
      return false;
    }
    // Don't scroll to top if there's a scrollTo query param - let component handle it
    if (to.query && to.query.scrollTo) {
      return false;
    }
    return { top: 0, left: 0, behavior: 'smooth' };
  }
});
let paths = [
  '/dashboard',
  '/id-verification',
  '/courses-checkout',
  '/courses-checkout-completion',
  '/my-courses',
  '/courses-history', // legacy path (redirects to /my-courses) — keep so old bookmarks are still captured for post-login redirect
  '/courses-add'
];

const originalRoutePush = router.push;
router.push = function push(location) {
  if (typeof location === 'string') location = { path: location };

  // Preserve query params for specific routes during registration flow
  const registrationRoutes = ['WelcomeDesktop', 'Registerv2', 'ConfirmRegistrationv2', 'SuccessRegistration'];
  // In Vue Router 4, currentRoute is a ref — must access via .value
  const currentQuery = this.currentRoute?.value?.query || {};

  if (location.name && registrationRoutes.includes(location.name) && Object.keys(currentQuery).length > 0) {
    // Only preserve query if not explicitly provided
    location.query = location.query || currentQuery;
  }

  return originalRoutePush.call(this, location).catch((err) => {
    if (err.name !== 'NavigationDuplicated') throw err;
  });
};

router.beforeEach(async (to, from, next) => {
  const retiredHostUrl = getRetiredHostRedirectUrl(window.location);
  if (retiredHostUrl) {
    next(false);
    window.location.replace(retiredHostUrl);
    return;
  }

  /**
   * if any other route which is not authenticated check for a silent session from Auth0
   * if loggedin, take the user to current router else login page
   */

  // Redirect logged-in users away from register page
  let isAuthenticated = auth.isAuthenticated();

  // Check for various auth tokens in localStorage
  const hasCustomOAuthToken = !!localStorage.getItem('customOAuthLoggedIn_token');
  const hasCognitoToken = !!localStorage.getItem('loggedIn');
  const hasAnyAuthToken =
    hasCustomOAuthToken ||
    hasCognitoToken ||
    !!localStorage.getItem('auth0Token') ||
    !!localStorage.getItem('awsToken');

  // Restore session if needed (localStorage says logged in but auth service has no tokens)
  if (!isAuthenticated && hasCognitoToken) {
    try {
      await auth.silentAuth();
      isAuthenticated = auth.isAuthenticated();
    } catch (error) {
      // Clear stale localStorage on failed session restore
      localStorage.removeItem('loggedIn');
    }
  }

  // Check multiple auth indicators
  const shouldRedirect = isAuthenticated === true || hasAnyAuthToken;

  if (to.path === '/register' && shouldRedirect) {
    // Allow registration if email query param is present (NO_CONTACT_FOUND scenario)
    if (to.query.email) {
      next();
      return;
    }

    // Capture pathway param for enrollment after redirect to dashboard
    if (to.query.pathway) {
      sessionStorage.setItem('pendingPathway', to.query.pathway);
    }

    // Capture certificate parameter before redirecting authenticated users (if feature enabled)
    if (to.query.certificate && isFeatureEnabled('CERTIFICATE_MANAGEMENT')) {
      // Store certificate parameter in sessionStorage for dashboard to process
      sessionStorage.setItem('pendingCertificate', to.query.certificate);
    }

    // Preserve viewAs parameter through the redirect so admin can register certificates for viewed-as users
    if (to.query.viewAs) {
      next({ path: '/dashboard', query: { viewAs: to.query.viewAs } });
      return;
    }

    next('/dashboard');
    return;
  }

  let requestedURL = window.location.href.split(window.location.origin)[1];

  if (requestedURL !== 'confirm-registration') {
    let captchaFrames = Array.from(document.getElementsByTagName('iframe'));
    let captchaArrow = Array.from(document.getElementsByClassName('g-recaptcha-bubble-arrow'));

    captchaFrames.forEach((frame) => {
      if (frame && frame.parentNode && frame.title == 'recaptcha challenge expires in two minutes') {
        frame.parentNode.style.display = 'none';
      }
    });

    captchaArrow.forEach((arrow) => {
      arrow.parentNode.style.display = 'none';
    });
  }

  const currentDomain = window.location.hostname;

  // Cookie-based staging access control
  const restrictedEnvironments = ['-staging', '-qa', '-dev', 'localhost'];
  if (restrictedEnvironments.some((env) => currentDomain.includes(env))) {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    };

    // Check for access key in URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const accessKey = urlParams.get('access');

    // If access key provided, set cookie and redirect to clean URL
    if (accessKey === 'staging2024') {
      document.cookie = 'staging_access=allowed; path=/; max-age=2592000'; // 30 days
      // Remove access parameter from URL and redirect
      urlParams.delete('access');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.location.replace(newUrl);
      return;
    }

    const stagingAccess = getCookie('staging_access');
    if (!stagingAccess || stagingAccess !== 'allowed') {
      // Show access blocked message with instructions
      document.body.innerHTML = `
        <div style="
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          background-color: #f5f5f5;
          font-family: Arial, sans-serif;
        ">
          <div style="
            background: white;
            border-radius: 8px;
            padding: 32px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            text-align: center;
            max-width: 600px;
            width: 90%;
          ">
            <div style="
              color: #8C1D40;
              font-size: 24px;
              font-weight: 600;
              margin-bottom: 16px;
            ">
              Access Restricted
            </div>
            <div style="
              color: #666;
              font-size: 16px;
              line-height: 1.5;
              margin-bottom: 24px;
            ">
              This is not the site you're looking for. Please visit our main site.
            </div>
            <a href="https://welcome.ea.asu.edu" style="
              display: inline-block;
              background-color: #8C1D40;
              color: white;
              padding: 12px 24px;
              border-radius: 4px;
              text-decoration: none;
              font-weight: 500;
            ">
              Go to Main Site
            </a>
          </div>
        </div>
      `;
      return; // Exit early since we're blocking access
    }
  }

  if (currentDomain.startsWith('edplus')) {
    //Edplus domain should redirect to Welcome domain
    const newUrl = window.location.href.replace('edplus', 'welcome');

    window.location.href = newUrl;
    return; // Exit early since we're redirecting
  }

  if (!auth.isAuthenticated()) {
    try {
      await auth.silentAuth();
      next();
    } catch (error) {
      next();
    }

    let requestedURL = window.location.href.split(window.location.origin)[1];

    for (const path of paths) {
      if (requestedURL.includes(path)) {
        localStorage.setItem('requestedURL', requestedURL);
      }
    }
  } else {
    // Minor consent gate (ACC-352): while the gate blocks the portal, keep the
    // learner on their current view (the modal overlays it) and block navigating
    // deeper into named content routes. A dismiss within the grace window clears
    // blocksPortal and frees navigation. Allowed through: the initial landing
    // (no `from.name`), auth/error routes in the exempt list, and unnamed routes
    // (e.g. /callback, /logout) so the learner can still authenticate or leave.
    const minorConsent = useMinorConsentStore();
    const gateExemptRoutes = ['login', 'error', 'server-error', 'browser-error'];
    const blockedByConsentGate = shouldBlockNavigationForConsent({
      blocksPortal: minorConsent.blocksPortal,
      fromName: from.name,
      toName: to.name,
      exemptRouteNames: gateExemptRoutes,
      isViewingAs: Boolean(ConnectorService?._isProxy)
    });
    if (blockedByConsentGate) {
      return next(false);
    }

    const registrationPaths = ['/register', '/confirm-registration'];
    if (!registrationPaths.some((path) => to.path === path)) {
      localStorage.removeItem('requestedURL');
    }

    if ((ConnectorService.learnerData || {})._contactId && ConnectorService._$window.dataLayer) {
      const virtualUrl = to.path;

      ConnectorService._$window.dataLayer.push({
        event: 'virtualPageView',
        virtualUrl,
        contactId: ConnectorService.learnerData._contactId
      });
    }

    next();
  }
});

// ULC-2844: Mark deep link cart pages as noindex to prevent SEO conflicts.
// URLs like /dashboard?course_offering_id=XXXX are transient (they redirect to
// checkout) but were being indexed by crawlers, causing duplicate page issues.
router.afterEach((to) => {
  const NOINDEX_PARAMS = ['course_offering_id'];
  const hasDeepLinkParam = NOINDEX_PARAMS.some((param) => to.query[param]);
  let robotsMeta = document.querySelector('meta[name="robots"]');

  if (hasDeepLinkParam) {
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex,nofollow');
  } else if (robotsMeta) {
    robotsMeta.remove();
  }
});

export default router;
